import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { brand } from "../../config/theme";
import { cn, DISABLED, FOCUS_RING } from "./cn";

export const AUDIO_PLAYER_SIZES = {
  sm: {
    button: "h-7 w-7",
    icon: "text-xs",
    time: "text-[9.5px]",
    track: "h-1",
    volume: "w-10",
    waveform: "h-10",
  },
  md: {
    button: "h-9 w-9",
    icon: "text-sm",
    time: "text-[10.5px]",
    track: "h-1.5",
    volume: "w-16",
    waveform: "h-14",
  },
} as const satisfies Record<string, Record<string, string>>;

export type AudioPlayerSize = keyof typeof AUDIO_PLAYER_SIZES;

const WAVEFORM_BUCKETS = 96;
const PLAYED_ALPHA = 0.9;
const PENDING_ALPHA = 0.22;

export interface AudioPlayerProps {
  src: string;
  size?: AudioPlayerSize;
  autoPlay?: boolean;
  crossOrigin?: "anonymous" | "use-credentials";
  duration?: number | null;
  waveform?: boolean;
  waveformTone?: string;
  playLabel: string;
  pauseLabel: string;
  seekLabel: string;
  muteLabel: string;
  unmuteLabel: string;
  volumeLabel: string;
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: () => void;
  onWaveformBlocked?: () => void;
}

const clock = (seconds: number) => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(safe / 60)}:${`${safe % 60}`.padStart(2, "0")}`;
};

const volumeIcon = (volume: number, muted: boolean) => {
  if (muted || volume === 0) return "ri-volume-mute-line";
  return volume < 0.5 ? "ri-volume-down-line" : "ri-volume-up-line";
};

const peaksOf = (buffer: AudioBuffer): number[] => {
  const samples = buffer.getChannelData(0);
  const perBucket = Math.max(Math.floor(samples.length / WAVEFORM_BUCKETS), 1);

  const peaks = Array.from({ length: WAVEFORM_BUCKETS }, (_, bucket) => {
    const from = bucket * perBucket;
    const to = Math.min(from + perBucket, samples.length);
    let peak = 0;
    for (let index = from; index < to; index += 1) {
      const value = Math.abs(samples[index]);
      if (value > peak) peak = value;
    }
    return peak;
  });

  const loudest = Math.max(...peaks);
  return loudest > 0 ? peaks.map((peak) => peak / loudest) : peaks;
};

export const AudioPlayer = forwardRef<HTMLAudioElement, AudioPlayerProps>(function AudioPlayer(
  {
    src,
    size = "sm",
    autoPlay,
    crossOrigin,
    duration,
    waveform = false,
    waveformTone = brand.primary,
    playLabel,
    pauseLabel,
    seekLabel,
    muteLabel,
    unmuteLabel,
    volumeLabel,
    className,
    onPlay,
    onPause,
    onEnded,
    onError,
    onWaveformBlocked,
  },
  ref,
) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blockedNotify = useRef(onWaveformBlocked);
  blockedNotify.current = onWaveformBlocked;

  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(duration ?? 0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [waveformBlocked, setWaveformBlocked] = useState(false);

  useImperativeHandle(ref, () => audioRef.current as HTMLAudioElement, []);

  useEffect(() => {
    setElapsed(0);
    setTotal(duration ?? 0);
  }, [src, duration]);

  useEffect(() => {
    const element = audioRef.current;
    if (!element) return;
    element.volume = volume;
    element.muted = muted;
  }, [volume, muted, src]);

  useEffect(() => {
    if (!waveform) return;

    const context = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(`${response.status}`);
        const decoded = await context.decodeAudioData(await response.arrayBuffer());
        if (!cancelled) setPeaks(peaksOf(decoded));
      } catch {
        if (cancelled) return;
        setWaveformBlocked(true);
        blockedNotify.current?.();
      } finally {
        void context.close();
      }
    };

    setPeaks(null);
    void load();

    return () => {
      cancelled = true;
    };
  }, [src, waveform]);

  const scale = AUDIO_PLAYER_SIZES[size];
  const progress = total > 0 ? Math.min((elapsed / total) * 100, 100) : 0;
  const level = muted ? 0 : volume;
  const showWaveform = waveform && !waveformBlocked;

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.round(canvas.clientWidth * dpr)) {
      canvas.width = Math.round(canvas.clientWidth * dpr);
      canvas.height = Math.round(canvas.clientHeight * dpr);
    }

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, width, height);

    const middle = height / 2;
    context.fillStyle = waveformTone;

    if (!peaks) {
      context.globalAlpha = PENDING_ALPHA;
      context.fillRect(0, middle - 0.5, width, 1);
      context.globalAlpha = 1;
      return;
    }

    const slot = width / peaks.length;
    const thickness = Math.max(slot - 1, 1);
    const playedBuckets = total > 0 ? (elapsed / total) * peaks.length : 0;

    peaks.forEach((peak, bucket) => {
      const bar = Math.max(peak * (middle - 1), 1);
      const x = bucket * slot + (slot - thickness) / 2;
      context.globalAlpha = bucket < playedBuckets ? PLAYED_ALPHA : PENDING_ALPHA;
      context.fillRect(x, middle - bar, thickness, bar * 2);
    });

    context.globalAlpha = 1;
  }, [peaks, elapsed, total, waveformTone]);

  useEffect(() => {
    if (!showWaveform) return;
    drawWaveform();
  }, [showWaveform, drawWaveform]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!showWaveform || !canvas || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => drawWaveform());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [showWaveform, drawWaveform]);

  const toggle = () => {
    const element = audioRef.current;
    if (!element) return;
    if (element.paused) void element.play();
    else element.pause();
  };

  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const element = audioRef.current;
    const next = Number(event.target.value);
    if (!element || !Number.isFinite(next)) return;
    element.currentTime = next;
    setElapsed(next);
  };

  const changeVolume = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    setVolume(next);
    setMuted(next === 0);
  };

  const seekInput = (
    <input
      type="range"
      min={0}
      max={total || 0}
      step={0.1}
      value={elapsed}
      onChange={seek}
      disabled={total === 0}
      aria-label={seekLabel}
      className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0 disabled:cursor-default"
    />
  );

  const controls = (
    <>
      <button
        type="button"
        onClick={toggle}
        title={playing ? pauseLabel : playLabel}
        aria-label={playing ? pauseLabel : playLabel}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-primary-500 text-on-primary transition-colors hover:bg-primary-400",
          scale.button,
          FOCUS_RING,
          DISABLED,
        )}
      >
        <i className={cn(playing ? "ri-pause-fill" : "ri-play-fill", scale.icon)} />
      </button>

      <span className={cn("shrink-0 font-mono tabular-nums text-content-faint", scale.time)}>
        {clock(elapsed)}
      </span>

      {showWaveform ? (
        <span className="min-w-0 flex-1" />
      ) : (
        <span className="relative flex min-w-0 flex-1 items-center">
          <span className={cn("w-full overflow-hidden rounded-full bg-subtle", scale.track)}>
            <span
              className="block h-full rounded-full bg-primary-500 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </span>
          {seekInput}
        </span>
      )}

      <span className={cn("shrink-0 font-mono tabular-nums text-content-faint", scale.time)}>
        {clock(total)}
      </span>

      <span className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => setMuted((current) => !current)}
          title={muted ? unmuteLabel : muteLabel}
          aria-label={muted ? unmuteLabel : muteLabel}
          aria-pressed={muted}
          className={cn(
            "flex items-center justify-center rounded-full text-content-faint transition-colors hover:text-content",
            scale.icon,
            FOCUS_RING,
          )}
        >
          <i className={volumeIcon(volume, muted)} />
        </button>

        <span className={cn("relative flex items-center", scale.volume)}>
          <span className={cn("w-full overflow-hidden rounded-full bg-subtle", scale.track)}>
            <span
              className="block h-full rounded-full bg-content-faint transition-[width] duration-150"
              style={{ width: `${level * 100}%` }}
            />
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={level}
            onChange={changeVolume}
            aria-label={volumeLabel}
            className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
          />
        </span>
      </span>
    </>
  );

  const media = (
    <audio
      ref={audioRef}
      src={src}
      autoPlay={autoPlay}
      crossOrigin={crossOrigin}
      preload="metadata"
      className="hidden"
      onLoadedMetadata={(event) => {
        const value = event.currentTarget.duration;
        if (Number.isFinite(value) && value > 0) setTotal(value);
      }}
      onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
      onVolumeChange={(event) => {
        setVolume(event.currentTarget.volume);
        setMuted(event.currentTarget.muted);
      }}
      onPlay={() => {
        setPlaying(true);
        onPlay?.();
      }}
      onPause={() => {
        setPlaying(false);
        onPause?.();
      }}
      onEnded={() => {
        setPlaying(false);
        setElapsed(0);
        onEnded?.();
      }}
      onError={() => {
        setPlaying(false);
        onError?.();
      }}
    >
      <track kind="captions" />
    </audio>
  );

  if (!showWaveform) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-full border border-line bg-surface px-2 py-1.5",
          className,
        )}
      >
        {controls}
        {media}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "space-y-1.5 rounded-2xl border border-line bg-surface px-2.5 py-2",
        className,
      )}
    >
      <span className="relative block w-full">
        <canvas ref={canvasRef} className={cn("block w-full", scale.waveform)} />
        {seekInput}
      </span>

      <div className="flex items-center gap-2">{controls}</div>
      {media}
    </div>
  );
});
